import { useState } from 'react'
import { MdAdd, MdRefresh } from 'react-icons/md'
import { Button, HStack, Icon, useDisclosure } from '@ritualco/jutsu'
import {
  useGetWorkspaceMembers,
  useInviteWorkspaceMembers,
  useRemoveWorkspaceMember,
  useResendInvitationToWorkspaceMember,
  useResendInvitationToWorkspaceMembers,
} from 'api/endpoints/workspaceMembers'
import { EditableSection } from 'components/EditableSection'
import { Pagination } from 'components/Pagination'
import { useMessage } from 'contexts/MessageContext'
import { useContent } from 'lib/content/react'
import Loading from 'components/Loading'
import { INITIAL_ROWS_PER_PAGE } from 'config'
import { EmployeeCreateModal } from 'components/Modals/EmployeeCreateModal'
import { MembersTable } from 'components/Tables/MembersTable'
import { FullWidthPage } from 'components/PageTemplates'

type EmployeesProps = {
  organizationId: string
  workspaceId: string
}

const Employees = ({ workspaceId, organizationId }: EmployeesProps) => {
  const { formatMessage } = useContent()
  const { addMessage } = useMessage()
  const [rowsPerPage, setRowsPerPage] = useState<number>(INITIAL_ROWS_PER_PAGE)
  const [paginationTokenOption, setPaginationTokenOption] = useState({})
  const { status, data } = useGetWorkspaceMembers({
    organizationId,
    workspaceId,
    limit: rowsPerPage,
    ...paginationTokenOption,
  })
  const create = useInviteWorkspaceMembers()
  const remove = useRemoveWorkspaceMember()
  const resend = useResendInvitationToWorkspaceMember()
  const resendAll = useResendInvitationToWorkspaceMembers()
  const { isOpen, onOpen, onClose } = useDisclosure()

  if (status === 'loading') {
    return <Loading />
  }

  if (status === 'error') {
    // TODO: handle error state
    return <span>Something went wrong!</span>
  }

  const showResendAllButton = data?.results.some(
    invitation => invitation.status === 'INVITED'
  )

  const handleInvite = (emails: string[]) => {
    create.mutate(
      {
        organizationId,
        workspaceId,
        workspaceMembersInviteRequest: { emails },
      },
      {
        onSuccess: () => {
          addMessage({
            status: 'success',
            description: formatMessage(
              {
                id:
                  emails.length > 1
                    ? 'pmportal.companies.employees.inviteMultipleSuccess'
                    : 'pmportal.companies.employees.inviteSingleSuccess',
              },
              {
                count: emails.length,
              }
            ),
            isClosable: false,
          })
        },
      }
    )
  }

  const handleRemove = (workspaceMemberId: string) => {
    remove.mutate(
      { organizationId, workspaceId, workspaceMemberId },
      {
        onSuccess: () => {
          addMessage({
            status: 'success',
            description: formatMessage({
              id: 'pmportal.companies.employees.removeSuccess',
            }),
            isClosable: false,
          })
        },
      }
    )
  }

  const handleResend = (workspaceMemberId: string) => {
    resend.mutate(
      { organizationId, workspaceId, workspaceMemberId },
      {
        onSuccess: () => {
          addMessage({
            status: 'success',
            description: formatMessage({
              id: 'pmportal.companies.resendSuccess',
            }),
            isClosable: false,
          })
        },
      }
    )
  }

  const handleResendAll = () => {
    resendAll.mutate(
      { organizationId, workspaceId },
      {
        onSuccess: data => {
          const count = data.results?.length || 0
          addMessage({
            status: 'success',
            description: formatMessage(
              { id: 'pmportal.companies.employees.resendAllSuccess' },
              { count }
            ),
            isClosable: false,
          })
        },
      }
    )
  }

  return (
    <FullWidthPage
      heading={formatMessage({ id: 'pmportal.companies.employees.title' })}
      showDivider={true}
    >
      <EditableSection
        action={
          <HStack>
            {showResendAllButton && (
              <Button
                colorScheme="transparent"
                variant="outline"
                leftIcon={<Icon as={MdRefresh} w="18px" h="18px" />}
                isLoading={resendAll.isLoading}
                onClick={handleResendAll}
              >
                {formatMessage({ id: 'pmportal.companies.resendAll' })}
              </Button>
            )}
            <Button
              colorScheme="transparent"
              variant="outline"
              leftIcon={<Icon as={MdAdd} w="18px" h="18px" />}
              onClick={onOpen}
            >
              {formatMessage({ id: 'pmportal.companies.employees.invite' })}
            </Button>
          </HStack>
        }
      >
        <MembersTable
          workspaceMembers={data?.results}
          onResend={handleResend}
          onRemove={handleRemove}
        />
        <Pagination
          rowsPerPage={rowsPerPage}
          hasNext={data?.hasNextPage}
          hasPrev={data?.hasPreviousPage}
          onRowsPerPageChange={e => setRowsPerPage(Number(e.target.value))}
          onPrev={() =>
            setPaginationTokenOption({
              previousPageToken: data?.paginationToken,
            })
          }
          onNext={() =>
            setPaginationTokenOption({ nextPageToken: data?.paginationToken })
          }
        />
      </EditableSection>
      <EmployeeCreateModal
        isOpen={isOpen}
        onClose={onClose}
        onSubmit={handleInvite}
      />
    </FullWidthPage>
  )
}
export default Employees
